import React, { useEffect, useState } from 'react';
import times from 'lodash/times';
import { numbersOnlyRegex } from '../../utils/regex';
import { getCardTypeSVGIcon } from '../../utils/creditCard';
import creditCardType, { getTypeInfo } from 'credit-card-type';
import '../../styles/paymentInfo.css';
import { Button, Input, Select, SelectItem } from '@nextui-org/react';

const EXPIRY_MONTHS = [{ label: '01', value: 1 }, { label: '02', value: 2 }, { label: '03', value: 3 }, { label: '04', value: 4 }, { label: '05', value: 5 }, { label: '06', value: 6 }, { label: '07', value: 7 }, { label: '08', value: 8 }, { label: '09', value: 9 }, { label: 10, value: 10 }, { label: 11, value: 11 }, { label: 12, value: 12 }];
const EXPIRY_YEARS = times(15, i => ({ value: new Date().getFullYear() + i, label: new Date().getFullYear() + i }));

const CreditCardForm = props => {
    const { submitPayment, creditCardFormError, setCreditCardFormError, isPaymentLoading, creditCardFormErrorMessage } = props;
    const [zipcode, setZipcode] = useState('');
    const [cvvNumber, setCvvNumber] = useState('');
    const [cardIcon, setCardIcon] = useState(<></>);
    const [expiryYear, setExpiryYear] = useState('');
    const [expiryMonth, setExpiryMonth] = useState('');
    const [zipcodeError, setZipcodeError] = useState(false);
    const [cardProperties, setCardProperties] = useState({});
    const [cvvNumberError, setCvvNumberError] = useState(false);
    const [creditCardNumber, setCreditCardNumber] = useState('');
    const [cardNumberError, setCardNumberError] = useState(false);

    const handleCreditCardInput = e => {
        const cardType = creditCardType(e.target.value)[0] || {};
        const prettyCard = prettyCardNumber(e.target.value.replace(/\s/g, ''), cardType.type);
        setCreditCardNumber(prettyCard);
        setCardProperties(cardType);
    };

    useEffect(() => {
        setCardIcon(getCardTypeSVGIcon(cardProperties.type));
    }, [cardProperties]);

    const validateForm = () => {
        const truncatedCard = creditCardNumber.replace(/\s/g, '');
        if (!numbersOnlyRegex.test(truncatedCard) || !cardProperties.lengths.includes(truncatedCard.length)) {
            setCardNumberError(true);
            return false;
        }
        if (!numbersOnlyRegex.test(cvvNumber) || cvvNumber.length !== cardProperties.code.size) {
            setCvvNumberError(true);
            return false;
        }
        if (!numbersOnlyRegex.test(zipcode) || zipcode.length !== 5) {
            setZipcodeError(true);
            return false;
        }
        return true;
    };

    const submitPaymentHandler = (event) => {
        event.preventDefault();
        if (validateForm()) {
            submitPayment({
                cvc: cvvNumber,
                exp_year: expiryYear,
                exp_month: expiryMonth,
                number: creditCardNumber.replace(/\s/g, ''),
            }, { zipcode });
        }
    };

    const prettyCardNumber = (cardNumber, cardType) => {
        const card = getTypeInfo(cardType);
        if (card) {
            const offsets = [].concat(0, card.gaps, cardNumber.length);
            const components = [];

            for (let i = 0; offsets[i] < cardNumber.length; i++) {
                const start = offsets[i];
                const end = Math.min(offsets[i + 1], cardNumber.length);
                components.push(cardNumber.substring(start, end));
            }
            return components.join(" ");
        }
        return cardNumber;
    };

    const truncate = str => (str.substring(0, 4));

    return <div className="flex-col">
        <div className="payment__container">
            <div className="credit-card__container">
                <h2>Card Details</h2>
            </div>
            <form className="credit-card__form" onSubmit={submitPaymentHandler}>
                <div className="credit-card__row" >
                    <div className="number__section">
                        <Input
                            isRequired
                            type="text"
                            name="credit-card"
                            variant="default"
                            label="Credit card"
                            textValue="string"
                            value={creditCardNumber}
                            isInvalid={cardNumberError}
                            aria-label="Credit card info"
                            onChange={handleCreditCardInput}
                            errorMessage={cardNumberError && "Credit card number is Invalid"}
                            onFocus={() => setCreditCardFormError(false)}
                         />
                    </div>
                </div>
                <div className="credit-card__row">
                    <div className="zipcode__section">
                        <Input
                            isRequired
                            type="text"
                            label="Zipcode"
                            name="zipcode"
                            value={zipcode}
                            aria-label="zipcode"
                            isInvalid={zipcodeError}
                            errorMessage={zipcodeError && "Invalid zipcode"}
                            onChange={e => setZipcode(e.target.value)}
                        />
                    </div>
                    <div className="cvv__section">
                        <Input
                            isRequired
                            name="cvv"
                            label="CVV"
                            type="text"
                            aria-label="cvv"
                            variant="default"
                            value={cvvNumber}
                            textValue="string"
                            isInvalid={cvvNumberError}
                            errorMessage={cvvNumberError && "Invalid"}
                            onChange={e => setCvvNumber(truncate(e.target.value))}
                            onFocus={() => setCvvNumberError(false)}
                        />
                    </div>
                </div>
                <div className="credit-card__row">
                    <div className="expiry__section">
                        <Select
                            isRequired
                            label="MM"
                            className="max-w-xs"
                            value={expiryMonth}
                            onChange={(e) => setExpiryMonth(e.target.value)}
                        >
                            {EXPIRY_MONTHS.map((month) => (
                                <SelectItem key={month.value} value={month.value}>
                                    {month.label}
                                </SelectItem>
                            ))}
                        </Select>
                        <Select
                            isRequired
                            label="YYYY"
                            aria-label="expiry year"
                            value={expiryYear}
                            className="max-w-xs"
                            onChange={(e) => setExpiryYear(e.target.value)}
                        >
                            {EXPIRY_YEARS.map((year) => (
                                <SelectItem key={year.value} value={year.value}>
                                    {year.label}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                </div>
                <div className="mb-6">
                    {creditCardFormError  && <span className="form-error mb-4">{creditCardFormErrorMessage}</span>}
                    <Button
                        type="submit"
                        color="primary"
                        isLoading={isPaymentLoading}
                        className="submit__payment w-full"
                    >
                        Start Subscription
                    </Button>
                </div>
            </form>
        </div>
        <div className="stripe__container">
            <img src="https://raw.githubusercontent.com/arvindrk/my-retro/master/images/stripe/Powered%20by%20Stripe%20-%20blurple.svg" alt="stripe" />
        </div>
    </div>;
};

export default CreditCardForm;